import { useMemo, useEffect, useRef, memo } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { DgaIcon } from "platformscode-new-react";
import { EServiceCardHome } from "./EServiceCardHome";

// ─────────────────────────────────────────────────────────────────────────────
// ARROW BUTTONS
// Destructure slick-specific props (currentSlide, slideCount) to prevent them
// from being forwarded to the DOM element and causing React warnings.
// ─────────────────────────────────────────────────────────────────────────────
const PrevArrow = memo(({ currentSlide, slideCount, ...domProps }) => (
  <button
    {...domProps}
    type="button"
    className="custom-arrow left-arrow"
    aria-label="الشريحة السابقة"
  >
    <DgaIcon
      icon="arrow-left-01"
      color="#fff"
      size={24}
      variant="stroke"
      aria-hidden="true"
    />
  </button>
));
PrevArrow.displayName = "PrevArrow";

const NextArrow = memo(({ currentSlide, slideCount, ...domProps }) => (
  <button
    {...domProps}
    type="button"
    className="custom-arrow right-arrow"
    aria-label="الشريحة التالية"
  >
    <DgaIcon
      icon="arrow-right-01"
      color="#fff"
      size={24}
      variant="stroke"
      aria-hidden="true"
    />
  </button>
));
NextArrow.displayName = "NextArrow";

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const CarouselSlider = ({ data = [], className = "" }) => {
  const containerRef = useRef(null);

  // Keep non-active slide buttons out of the tab order so keyboard users
  // don't land on off-screen content. We use tabIndex instead of `inert`
  // because react-slick clones slides for infinite-loop transitions: the
  // clones receive slick-active (so inert was removed from them) while the
  // REAL slides — which hold the React event listeners — got inert applied,
  // silently swallowing all clicks on the card buttons.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const applyTabIndex = () => {
      container.querySelectorAll(".slick-slide").forEach((slide) => {
        const isActive = slide.classList.contains("slick-active");
        slide.querySelectorAll("a, button, [tabindex]").forEach((el) => {
          el.tabIndex = isActive ? 0 : -1;
        });
      });
    };

    const observer = new MutationObserver(applyTabIndex);
    observer.observe(container, { subtree: true, attributeFilter: ["class"] });

    setTimeout(applyTabIndex, 100);

    return () => observer.disconnect();
  }, []);

  // Deduplicate by title — memoized so it doesn't recompute on every render
  const uniqueData = useMemo(
    () => data.filter((item, index, self) =>
      index === self.findIndex(s => s.title === item.title)
    ),
    [data]
  );

  const count = uniqueData.length;
  const canLoop = count > 1;

  // Settings object memoized — only recomputes when count changes
  const settings = useMemo(() => ({
    rtl:           true,
    accessibility: true,
    dots:          true,
    arrows:        true,
    infinite:      canLoop,
    customPaging:  (i) => (
      <button
        type="button"
        aria-label={`الشريحة ${i + 1} من ${count}`}
      />
    ),
    autoplay:      canLoop,
    autoplaySpeed: 2600,
    pauseOnHover:  true,
    pauseOnFocus:  true,
    slidesToShow:  Math.min(3, count),
    centerMode:    false,
    centerPadding: "0px",
    prevArrow:     <PrevArrow />,
    nextArrow:     <NextArrow />,
    responsive: [
      {
        breakpoint: 1280,
        settings: { slidesToShow: Math.min(3, count), arrows: true, dots: true },
      },
      {
        breakpoint: 1024,
        settings: { slidesToShow: Math.min(2, count), arrows: true, dots: false },
      },
      {
        breakpoint: 760,
        settings: { slidesToShow: 1, arrows: true, dots: false },
      },
    ],
  }), [count, canLoop]);

  if (!count) return null;

  return (
    <div
      ref={containerRef}
      className={`cw-full ${className}`.trim()}
      aria-label="عرض الخدمات الإلكترونية"
      aria-roledescription="carousel"
    >
      <Slider {...settings}>
        {uniqueData.map((service, index) => (
          // dir="rtl" is required: react-slick sets dir="ltr" on its internal
          // track when rtl:true, which would otherwise flip card text direction.
          <div
            key={service.title}
            dir="rtl"
            role="group"
            aria-roledescription="slide"
            aria-label={`${index + 1} / ${count}: ${service.title || ""}`}
          >
            <EServiceCardHome index={index} data={service} />
          </div>
        ))}
      </Slider>
    </div>
  );
};

export default CarouselSlider;
